import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiCopy,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiZap,
  FiClock,
  FiMail,
  FiMessageSquare,
  FiBell,
  FiPhone,
  FiFileText,
  FiAlertTriangle,
  FiRefreshCw,
  FiUsers,
  FiSearch,
  FiBarChart2,
  FiEdit3,
  FiTrendingUp,
  FiHash,
  FiUser
} from 'react-icons/fi';
import { useWorkflows } from '../../hooks/useWorkflows';
import MacSubscribePrompt from './MacSubscribePrompt';
import { getMacSsoToken } from '../../services/workflowService';
import type {
  Workflow,
  PublicWorkflow,
  WorkflowTouchpoint
} from '../../services/workflowService';
import './WorkflowsGrid.css';

// ── Workflow status colours ───────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  running: '#10b981',
  draft: '#f59e0b',
  paused: '#3b82f6',
  stopped: '#6b7280'
};

function getStatusColor(status: string): string {
  return STATUS_COLORS[status] ?? '#6b7280';
}

// ── Touchpoint type colours (from 20miles tpData.js) ─────────────────────────
const TOUCHPOINT_COLORS: Record<string, string> = {
  email: '#34875a',
  manual: '#006de0',
  sms: '#a0522d',
  call: '#7c3aed',
  other: '#d97706',
  notification: '#374151',
  nowcerts_task: '#006de0',
  opportunity_stage: '#e5a343',
  create_opportunity: '#34875a',
  add_tag: '#04bb99',
  remove_tag: '#bd3d00',
  insured_note: '#8b5cf6',
  webhook_trigger: '#0ea5e9',
  clone_policy_as_quote: '#6b7280',
  loss_run: '#f43f5e',
  agent_assignment: '#f59e0b'
};

function getTouchpointColor(type: string): string {
  return TOUCHPOINT_COLORS[type] ?? '#6b7280';
}

// ── Touchpoint display labels ─────────────────────────────────────────────────
const TOUCHPOINT_LABELS: Record<string, string> = {
  email: 'Automated Email',
  manual: 'Manual Email',
  sms: 'Automated SMS',
  call: 'Phone Call',
  other: 'Instructions',
  notification: 'Notification',
  nowcerts_task: 'Create Task',
  opportunity_stage: 'Change Stage',
  create_opportunity: 'Create Opportunity',
  add_tag: 'Add Tag',
  remove_tag: 'Remove Tag',
  insured_note: 'Note',
  webhook_trigger: 'Webhook',
  clone_policy_as_quote: 'Clone Policy',
  loss_run: 'Loss Run',
  agent_assignment: 'Agent / CSR Assignment'
};

function getTouchpointLabel(type: string): string {
  return TOUCHPOINT_LABELS[type] ?? type;
}

// ── Touchpoint type → icon ────────────────────────────────────────────────────
function TouchpointIcon({ type, size = 12 }: { type: string; size?: number }) {
  const icons: Record<string, React.ReactElement> = {
    email: <FiMail size={size} />,
    manual: <FiEdit3 size={size} />,
    sms: <FiMessageSquare size={size} />,
    notification: <FiBell size={size} />,
    call: <FiPhone size={size} />,
    other: <FiFileText size={size} />,
    nowcerts_task: <FiFileText size={size} />,
    opportunity_stage: <FiTrendingUp size={size} />,
    create_opportunity: <FiTrendingUp size={size} />,
    add_tag: <FiHash size={size} />,
    remove_tag: <FiHash size={size} />,
    insured_note: <FiEdit3 size={size} />,
    webhook_trigger: <FiZap size={size} />,
    clone_policy_as_quote: <FiCopy size={size} />,
    loss_run: <FiBarChart2 size={size} />,
    agent_assignment: <FiUser size={size} />
  };
  return icons[type] ?? <FiZap size={size} />;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function truncate(str: string, max: number): string {
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function humaniseFilter(filter: string | undefined): string {
  if (!filter) return 'No trigger';
  return filter.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTrigger(trigger: Workflow['workflow_trigger']): string {
  if (!trigger) return 'No trigger';
  const category = trigger.filter_condition?.trigger_category?.name;
  const condition = trigger.filter_condition?.name;
  if (category && condition)
    return `${category} — ${humaniseFilter(condition)}`;
  return humaniseFilter(trigger.filter);
}

// ── Type-specific detail line for a touchpoint ────────────────────────────────
function getTpDetail(tp: WorkflowTouchpoint): string | null {
  const attr = tp.emails_attributes?.[0];
  const custom = tp.custom_task_attributes;

  switch (tp.type) {
    case 'email':
    case 'manual':
    case 'notification':
      return attr?.subject ? truncate(attr.subject, 60) : null;

    case 'sms':
      return attr?.body ? truncate(attr.body, 60) : null;

    case 'call':
    case 'other':
      return tp.content ? truncate(tp.content, 60) : null;

    case 'nowcerts_task': {
      const parts = [
        custom?.category_name,
        custom?.descriptive_time,
        custom?.assigne_type
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }

    case 'opportunity_stage':
      return custom?.stage ? `Stage: ${custom.stage}` : null;

    case 'create_opportunity': {
      const parts = [custom?.stage, custom?.assigne_type].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }

    case 'insured_note': {
      const parts = [
        custom?.note_type,
        custom?.subject ? truncate(custom.subject, 50) : null
      ].filter(Boolean);
      return parts.length ? parts.join(' — ') : null;
    }

    case 'webhook_trigger':
      return custom?.webhook_url
        ? `${custom.webhook_action_type ?? 'POST'} ${truncate(custom.webhook_url, 40)}`
        : null;

    case 'clone_policy_as_quote': {
      const parts = [
        custom?.billing_type,
        custom?.copy_selected_items?.length
          ? custom.copy_selected_items.join(', ')
          : null
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }

    case 'loss_run': {
      const parts = [
        custom?.nowcerts_carrier_mga?.insurance_company,
        custom?.email_template?.name,
        custom?.reminder_days != null
          ? `Remind after ${custom.reminder_days}d`
          : null
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : null;
    }

    case 'agent_assignment':
      return 'Assigns agents / CSRs';

    case 'add_tag':
    case 'remove_tag':
      return null;

    default:
      return null;
  }
}

// ── Unique touchpoint types from a workflow's touchpoints array ───────────────
function uniqueTouchpointTypes(touchpoints: WorkflowTouchpoint[]): string[] {
  const seen = new Set<string>();
  return touchpoints
    .map(t => t.type)
    .filter(type => {
      if (seen.has(type)) return false;
      seen.add(type);
      return true;
    });
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const color = getStatusColor(status);
  return (
    <span
      className='wf-badge'
      style={{
        background: color + '1a',
        color,
        border: `1px solid ${color}44`
      }}
    >
      {status}
    </span>
  );
};

// ── CloneButton (for public workflows — keyed by workflow.key) ───────────────
const CloneButton: React.FC<{
  id: string;
  isCloning: string | null;
  cloneSuccess: string | null;
  cloneError: string | null;
  onClone: (id: string) => void;
  size?: 'sm' | 'md';
  notSubscribed?: boolean;
}> = ({
  id,
  isCloning,
  cloneSuccess,
  cloneError,
  onClone,
  size = 'sm',
  notSubscribed
}) => {
  const [subscribing, setSubscribing] = useState(false);
  const isThisCloning = isCloning === id;
  const isSuccess = cloneSuccess === id;
  const isError = cloneError === id;

  if (notSubscribed) {
    const handleSubscribe = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setSubscribing(true);
      try {
        const amsToken = localStorage.getItem('amsToken') ?? '';
        const ssoToken = await getMacSsoToken(amsToken);
        window.open(
          `${(process.env.REACT_APP_FUSIONNOW_APP_URL)}/now-certs-auth?token=${encodeURIComponent(ssoToken)}&redirectfrom=myamp&signup=true`,
          '_blank'
        );
      } finally {
        setSubscribing(false);
      }
    };
    return (
      <motion.button
        onClick={handleSubscribe}
        disabled={subscribing}
        whileHover={subscribing ? {} : { scale: 1.03 }}
        whileTap={subscribing ? {} : { scale: 0.97 }}
        className={`wf-copy-btn wf-copy-btn--subscribe${size === 'md' ? ' wf-copy-btn--md' : ''}`}
        style={{
          cursor: subscribing ? 'not-allowed' : 'pointer'
        }}
      >
        <FiZap size={size === 'md' ? 13 : 11} />
        {subscribing ? 'Opening…' : 'Subscribe to Clone'}
      </motion.button>
    );
  }

  let bg = 'rgba(34,211,238,0.10)';
  let border = 'rgba(34,211,238,0.25)';
  let color = 'var(--accent-cyan, #22d3ee)';
  let label = 'Clone';

  if (isThisCloning) {
    bg = 'rgba(34,211,238,0.08)';
    label = 'Cloning…';
  } else if (isSuccess) {
    bg = 'rgba(52,211,153,0.12)';
    border = 'rgba(52,211,153,0.35)';
    color = '#34d399';
    label = 'Cloned!';
  } else if (isError) {
    bg = 'rgba(239,68,68,0.12)';
    border = 'rgba(239,68,68,0.35)';
    color = '#ef4444';
    label = 'Failed';
  }

  return (
    <motion.button
      onClick={e => {
        e.stopPropagation();
        onClone(id);
      }}
      disabled={isThisCloning}
      whileHover={isThisCloning ? {} : { scale: 1.03 }}
      whileTap={isThisCloning ? {} : { scale: 0.97 }}
      className={`wf-copy-btn${size === 'md' ? ' wf-copy-btn--md' : ''}`}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        color,
        cursor: isThisCloning ? 'not-allowed' : 'pointer'
      }}
    >
      {isThisCloning ? (
        <motion.span
          className='wf-copy-btn__spinner'
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
        >
          <FiRefreshCw size={12} />
        </motion.span>
      ) : isSuccess ? (
        <FiCheck size={12} />
      ) : (
        <FiCopy size={12} />
      )}
      {label}
    </motion.button>
  );
};

// ── ViewInMacButton (for My Workflows — keyed by workflow.key) ───────────────
const ViewInMacButton: React.FC<{
  wfKey: string;
  isViewingInMac: string | null;
  viewInMacError: string | null;
  onView: (key: string) => void;
  size?: 'sm' | 'md';
}> = ({ wfKey, isViewingInMac, viewInMacError, onView, size = 'sm' }) => {
  const isThisLoading = isViewingInMac === wfKey;
  const isError = viewInMacError === wfKey;

  let bg = 'rgba(139,92,246,0.10)';
  let border = 'rgba(139,92,246,0.25)';
  let color = '#8b5cf6';
  let label = 'View In MAC';

  if (isThisLoading) {
    bg = 'rgba(139,92,246,0.08)';
    label = 'Opening…';
  } else if (isError) {
    bg = 'rgba(239,68,68,0.12)';
    border = 'rgba(239,68,68,0.35)';
    color = '#ef4444';
    label = 'Failed';
  }

  return (
    <motion.button
      onClick={e => {
        e.stopPropagation();
        onView(wfKey);
      }}
      disabled={isThisLoading}
      whileHover={isThisLoading ? {} : { scale: 1.03 }}
      whileTap={isThisLoading ? {} : { scale: 0.97 }}
      className={`wf-copy-btn${size === 'md' ? ' wf-copy-btn--md' : ''}`}
      style={{
        background: bg,
        border: `1px solid ${border}`,
        color,
        cursor: isThisLoading ? 'not-allowed' : 'pointer'
      }}
    >
      {isThisLoading ? (
        <motion.span
          className='wf-copy-btn__spinner'
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
        >
          <FiRefreshCw size={12} />
        </motion.span>
      ) : (
        <FiZap size={12} />
      )}
      {label}
    </motion.button>
  );
};

// ── WorkflowCard (My Workflows — with View In MAC button) ────────────────────
const WorkflowCard: React.FC<{
  workflow: Workflow;
  isViewingInMac: string | null;
  viewInMacError: string | null;
  onView: (key: string) => void;
  onClick: () => void;
}> = ({ workflow, isViewingInMac, viewInMacError, onView, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const statusColor = getStatusColor(workflow.status);
  const tpTypes = uniqueTouchpointTypes(workflow.touchpoints);

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className='wf-card'
      style={{
        border: hovered ? `1px solid ${statusColor}50` : undefined,
        boxShadow: hovered ? `0 0 20px ${statusColor}18` : undefined
      }}
    >
      <div className='wf-card__status'>
        <StatusBadge status={workflow.status} />
      </div>

      <div
        className='wf-card__icon'
        style={{ background: statusColor + '22', color: statusColor }}
      >
        <FiZap size={20} />
      </div>

      <h3 className='wf-card__title'>{workflow.name}</h3>

      <div className='wf-card__meta'>
        <span className='wf-card__meta-contacts'>
          <FiUsers size={11} />
          {workflow.workflow_contacts_count}
        </span>
        <span className='wf-card__meta-trigger'>
          {formatTrigger(workflow.workflow_trigger)}
        </span>
      </div>

      <div className='wf-card__bottom'>
        <div className='wf-card__tp-list'>
          {tpTypes.slice(0, 3).map(type => {
            const tpColor = getTouchpointColor(type);
            return (
              <span
                key={type}
                title={getTouchpointLabel(type)}
                className='wf-card__tp-chip'
                style={{
                  background: tpColor + '18',
                  color: tpColor,
                  border: `1px solid ${tpColor}35`
                }}
              >
                <TouchpointIcon type={type} size={12} />
              </span>
            );
          })}
          {tpTypes.length > 3 && (
            <span className='wf-card__tp-more'>+{tpTypes.length - 3}</span>
          )}
        </div>
        <ViewInMacButton
          wfKey={workflow.key}
          isViewingInMac={isViewingInMac}
          viewInMacError={viewInMacError}
          onView={onView}
        />
      </div>
    </motion.div>
  );
};

// ── PublicWorkflowCard (Public Workflows — with Clone button) ─────────────────
const PublicWorkflowCard: React.FC<{
  workflow: PublicWorkflow;
  isCloning: string | null;
  cloneSuccess: string | null;
  cloneError: string | null;
  onClone: (id: string) => void;
  onClick: () => void;
  notSubscribed?: boolean;
}> = ({
  workflow,
  isCloning,
  cloneSuccess,
  cloneError,
  onClone,
  onClick,
  notSubscribed
}) => {
  const [hovered, setHovered] = useState(false);
  const statusColor = getStatusColor(workflow.status);
  const tpTypes = uniqueTouchpointTypes(workflow.touchpoints);

  return (
    <motion.div
      onClick={onClick}
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.18 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className='wf-card'
      style={{
        border: hovered ? `1px solid ${statusColor}50` : undefined,
        boxShadow: hovered ? `0 0 20px ${statusColor}18` : undefined
      }}
    >
      <div className='wf-card__status'>
        <StatusBadge status={workflow.status} />
      </div>

      <div
        className='wf-card__icon'
        style={{ background: statusColor + '22', color: statusColor }}
      >
        <FiZap size={20} />
      </div>

      <h3 className='wf-card__title'>{workflow.name}</h3>

      <div className='wf-card__meta'>
        <span className='wf-card__meta-contacts'>
          <FiUsers size={11} />
          {workflow.workflow_contacts_count}
        </span>
        <span className='wf-card__meta-trigger'>
          {formatTrigger(workflow.workflow_trigger)}
        </span>
      </div>

      <div className='wf-card__bottom'>
        <div className='wf-card__tp-list'>
          {tpTypes.slice(0, 3).map(type => {
            const tpColor = getTouchpointColor(type);
            return (
              <span
                key={type}
                title={getTouchpointLabel(type)}
                className='wf-card__tp-chip'
                style={{
                  background: tpColor + '18',
                  color: tpColor,
                  border: `1px solid ${tpColor}35`
                }}
              >
                <TouchpointIcon type={type} size={12} />
              </span>
            );
          })}
          {tpTypes.length > 3 && (
            <span className='wf-card__tp-more'>+{tpTypes.length - 3}</span>
          )}
        </div>
        <CloneButton
          id={workflow.key}
          isCloning={isCloning}
          cloneSuccess={cloneSuccess}
          cloneError={cloneError}
          onClone={onClone}
          notSubscribed={notSubscribed}
        />
      </div>
    </motion.div>
  );
};

// ── WorkflowDetail ────────────────────────────────────────────────────────────
const WorkflowDetail: React.FC<{
  workflow: Workflow;
  actionButton?: React.ReactNode;
  onBack: () => void;
  showStats?: boolean;
}> = ({ workflow, actionButton, onBack, showStats = true }) => {
  const statusColor = getStatusColor(workflow.status);
  const tpTypes = uniqueTouchpointTypes(workflow.touchpoints);

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2 }}
      className='wf-detail'
    >
      <motion.button
        onClick={onBack}
        whileHover={{ x: -2 }}
        className='wf-detail__back'
      >
        <FiChevronLeft size={15} />
        Back to Workflows
      </motion.button>

      {/* Header */}
      <div className='wf-detail__header'>
        <div
          className='wf-detail__icon'
          style={{ background: statusColor + '22', color: statusColor }}
        >
          <FiZap size={26} />
        </div>
        <div className='wf-detail__title-wrap'>
          <div className='wf-detail__title-row'>
            <h1 className='wf-detail__title'>{workflow.name}</h1>
            <StatusBadge status={workflow.status} />
          </div>
          {workflow.running_state && (
            <span className='wf-detail__running-state'>
              {workflow.running_state}
            </span>
          )}
        </div>
        {actionButton}
      </div>

      {/* Creator */}
      <div className='wf-detail__creator'>
        <img
          src={workflow.user.avatar?.url}
          alt={workflow.user.name}
          className='wf-detail__creator-avatar'
          onError={e => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
        <span className='wf-detail__creator-meta'>
          {workflow.user.name} · Updated{' '}
          {new Date(workflow.updated_at).toLocaleDateString()}
        </span>
      </div>

      {/* Stats */}
      {showStats && (
        <div className='wf-detail__stats'>
          {[
            {
              icon: <FiZap size={15} style={{ color: statusColor }} />,
              value: tpTypes.length,
              label: 'Steps'
            },
            {
              icon: <FiUsers size={15} className='wf-detail__stat-icon--contacts' />,
              value: workflow.workflow_contacts_count,
              label: 'Contacts'
            },
            {
              icon: <FiSearch size={15} className='wf-detail__stat-icon--opened' />,
              value: workflow.opened_count,
              label: 'Opened'
            },
            {
              icon: <FiBarChart2 size={15} className='wf-detail__stat-icon--clicked' />,
              value: workflow.clicked_count,
              label: 'Clicked'
            }
          ].map(({ icon, value, label }) => (
            <div key={label} className='wf-detail__stat-tile'>
              {icon}
              <div>
                <div className='wf-detail__stat-value'>{value}</div>
                <div className='wf-detail__stat-label'>{label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trigger */}
      {workflow.workflow_trigger && (
        <div className='wf-detail__section'>
          <div className='wf-detail__section-label'>Trigger</div>
          <span
            className='wf-detail__trigger-chip'
            style={{
              background: statusColor + '14',
              color: statusColor,
              border: `1px solid ${statusColor}30`
            }}
          >
            <FiClock size={11} />
            {formatTrigger(workflow.workflow_trigger)}
          </span>
        </div>
      )}

      {/* Touchpoints timeline */}
      <div>
        <div className='wf-detail__section-label'>
          Touchpoints ({workflow.touchpoints.length})
        </div>
        <div className='wf-detail__tp-timeline'>
          {workflow.touchpoints.map((tp, i) => {
            const isLast = i === workflow.touchpoints.length - 1;
            const nextTp = workflow.touchpoints[i + 1];
            const dayGap = nextTp ? nextTp.delay - tp.delay : null;
            const tpColor = getTouchpointColor(tp.type);
            const tpDetail = getTpDetail(tp);

            return (
              <div
                key={tp.id}
                className={`wf-detail__tp-step${isLast ? ' wf-detail__tp-step--last' : ''}`}
              >
                <div className='wf-detail__tp-track'>
                  <div
                    className='wf-detail__tp-dot'
                    style={{
                      background: tpColor + '20',
                      color: tpColor,
                      border: `1.5px solid ${tpColor}55`
                    }}
                  >
                    {i + 1}
                  </div>
                  {!isLast && (
                    <div
                      className='wf-detail__tp-connector'
                      style={{ background: tpColor + '30' }}
                    />
                  )}
                </div>

                <div className='wf-detail__tp-card'>
                  <div
                    className='wf-detail__tp-card-icon'
                    style={{ background: tpColor + '18', color: tpColor }}
                  >
                    <TouchpointIcon type={tp.type} size={16} />
                  </div>
                  <div className='wf-detail__tp-card-body'>
                    <span className='wf-detail__tp-card-type'>
                      {getTouchpointLabel(tp.type)}
                    </span>
                    {tpDetail && (
                      <span className='wf-detail__tp-card-detail'>
                        {tpDetail}
                      </span>
                    )}
                    {dayGap !== null && dayGap > 0 && (
                      <span className='wf-detail__tp-card-gap'>
                        +{dayGap} day{dayGap !== 1 ? 's' : ''} until next step
                      </span>
                    )}
                  </div>
                  <div className='wf-detail__tp-card-day'>Day {tp.delay}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// ── Pagination helpers ────────────────────────────────────────────────────────
function buildPageWindow(current: number, total: number): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  const pages: (number | null)[] = [1];

  if (left > 2) pages.push(null);
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push(null);
  pages.push(total);

  return pages;
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
const SKELETON_LINE_WIDTHS = ['40px', '65%', '85%', '85%', '40%'];

const SkeletonCard: React.FC = () => (
  <div className='wf-skeleton'>
    {[40, 16, 12, 12, 10].map((h, i) => (
      <div
        key={i}
        className='wf-skeleton__line'
        style={{ height: h, width: SKELETON_LINE_WIDTHS[i] }}
      />
    ))}
  </div>
);

// ── Pagination bar (reusable) ─────────────────────────────────────────────────
const PaginationBar: React.FC<{
  currentPage: number;
  totalPages: number;
  totalCount: number;
  label: string;
  onGoTo: (page: number) => void;
}> = ({ currentPage, totalPages, totalCount, label, onGoTo }) => {
  const pageWindow = buildPageWindow(currentPage, totalPages);
  return (
    <div className='wf-pagination'>
      <div className='wf-pagination__info'>
        Page {currentPage} of {totalPages} · {totalCount} {label}
      </div>

      <button
        className='wf-pagination__btn'
        onClick={() => onGoTo(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label='Previous page'
      >
        <FiChevronLeft size={14} />
      </button>

      {pageWindow.map((page, i) =>
        page === null ? (
          <span key={`ellipsis-${i}`} className='wf-pagination__ellipsis'>
            …
          </span>
        ) : (
          <button
            key={page}
            className={`wf-pagination__btn${page === currentPage ? ' wf-pagination__btn--active' : ''}`}
            onClick={() => onGoTo(page)}
            disabled={page === currentPage}
          >
            {page}
          </button>
        )
      )}

      <button
        className='wf-pagination__btn'
        onClick={() => onGoTo(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label='Next page'
      >
        <FiChevronRight size={14} />
      </button>
    </div>
  );
};

// ── WorkflowsGrid (exported) ──────────────────────────────────────────────────
const WorkflowsGrid: React.FC = () => {
  const {
    workflows,
    meta,
    isLoading,
    error,
    publicWorkflows,
    publicMeta,
    isLoadingPublic,
    publicError,
    notSubscribed,
    searchQuery,
    setSearchQuery,
    isCloning,
    cloneSuccess,
    cloneError,
    isViewingInMac,
    viewInMacError,
    fetchWorkflows,
    fetchPublicWorkflows,
    goToPage,
    goToPublicPage,
    cloneWorkflow,
    viewInMac
  } = useWorkflows();

  const [selected, setSelected] = useState<Workflow | null>(null);
  const [selectedPublic, setSelectedPublic] = useState<PublicWorkflow | null>(
    null
  );

  // ── Detail view: Public Workflow ──────────────────────────────────────────
  if (selectedPublic) {
    return (
      <WorkflowDetail
        workflow={selectedPublic as unknown as Workflow}
        actionButton={
          <CloneButton
            id={selectedPublic.key}
            isCloning={isCloning}
            cloneSuccess={cloneSuccess}
            cloneError={cloneError}
            onClone={cloneWorkflow}
            size='md'
            notSubscribed={notSubscribed}
          />
        }
        onBack={() => setSelectedPublic(null)}
        showStats={false}
      />
    );
  }

  // ── Detail view: My Workflow ──────────────────────────────────────────────
  if (selected) {
    return (
      <WorkflowDetail
        workflow={selected}
        actionButton={
          <ViewInMacButton
            wfKey={selected.key}
            isViewingInMac={isViewingInMac}
            viewInMacError={viewInMacError}
            onView={viewInMac}
            size='md'
          />
        }
        onBack={() => setSelected(null)}
      />
    );
  }

  const myPage = meta?.current_page ?? 1;
  const myTotal = meta?.total_pages ?? 1;
  const myCount = meta?.total_count ?? 0;

  const pubPage = publicMeta?.current_page ?? 1;
  const pubTotal = publicMeta?.total_pages ?? 1;
  const pubCount = publicMeta?.total_count ?? 0;

  return (
    <div className='wf-grid'>
      {/* Search bar — shared, always visible */}
      <div className='wf-search'>
        <FiSearch size={13} className='wf-search__icon' />
        <input
          type='text'
          className='wf-search__input'
          placeholder='Search workflows...'
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            className='wf-search__clear'
            onClick={() => setSearchQuery('')}
            aria-label='Clear search'
          >
            <FiRefreshCw size={11} />
          </button>
        )}
      </div>

      {/* ── Combined empty state ──────────────────────────────────────────── */}
      {!isLoadingPublic &&
        !isLoading &&
        publicWorkflows.length === 0 &&
        workflows.length === 0 &&
        !publicError &&
        !error &&
        !notSubscribed && (
          <div className='wf-empty'>
            <FiSearch size={32} className='wf-empty__icon' />
            {searchQuery.trim() ? (
              <>
                <p className='wf-empty__title'>
                  No workflows match "{searchQuery}"
                </p>
                <p className='wf-empty__sub'>
                  Try a different name or clear the search
                </p>
              </>
            ) : (
              <p className='wf-empty__title'>No workflows found</p>
            )}
          </div>
        )}

      {/* ── Global Workflows section ──────────────────────────────────────── */}
      {(isLoadingPublic || publicWorkflows.length > 0 || !!publicError) && (
        <>
          <div className='wf-section-title wf-section-title--public'>
            Global Workflows
          </div>

          {publicError && (
            <div className='wf-grid__error'>
              <FiAlertTriangle size={15} />
              {publicError}
              <button className='wf-grid__retry' onClick={fetchPublicWorkflows}>
                <FiRefreshCw size={12} /> Retry
              </button>
            </div>
          )}

          <div className='wf-grid__cards'>
            {isLoadingPublic
              ? Array.from({ length: 12 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))
              : publicWorkflows.map(workflow => (
                  <PublicWorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    isCloning={isCloning}
                    cloneSuccess={cloneSuccess}
                    cloneError={cloneError}
                    onClone={cloneWorkflow}
                    onClick={() => setSelectedPublic(workflow)}
                    notSubscribed={notSubscribed}
                  />
                ))}
          </div>

          {!isLoadingPublic &&
            !searchQuery.trim() &&
            publicMeta &&
            pubTotal > 1 && (
              <PaginationBar
                currentPage={pubPage}
                totalPages={pubTotal}
                totalCount={pubCount}
                label='workflows'
                onGoTo={goToPublicPage}
              />
            )}
        </>
      )}

      {/* ── My Workflows section ─────────────────────────────────────────── */}
      {(isLoading || workflows.length > 0 || !!error || notSubscribed) && (
        <>
          <div className='wf-section-title wf-section-title--my'>
            My Workflows
          </div>

          {notSubscribed ? (
            <MacSubscribePrompt />
          ) : (
            <>
              {error && (
                <div className='wf-grid__error'>
                  <FiAlertTriangle size={15} />
                  {error}
                  <button className='wf-grid__retry' onClick={fetchWorkflows}>
                    <FiRefreshCw size={12} /> Retry
                  </button>
                </div>
              )}

              <div className='wf-grid__cards'>
                {isLoading
                  ? Array.from({ length: 12 }).map((_, i) => (
                      <SkeletonCard key={i} />
                    ))
                  : workflows.map(workflow => (
                      <WorkflowCard
                        key={workflow.id}
                        workflow={workflow}
                        isViewingInMac={isViewingInMac}
                        viewInMacError={viewInMacError}
                        onView={viewInMac}
                        onClick={() => setSelected(workflow)}
                      />
                    ))}
              </div>

              {!isLoading && !searchQuery.trim() && meta && myTotal > 1 && (
                <PaginationBar
                  currentPage={myPage}
                  totalPages={myTotal}
                  totalCount={myCount}
                  label='workflows'
                  onGoTo={goToPage}
                />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default WorkflowsGrid;

